import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CalendarClock, X } from "lucide-react";
import { CALENDLY_URL } from "@/lib/booking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * "Book a demo appointment" button. Opens the Calendly embed in a popup when
 * CALENDLY_URL is configured (visitor never leaves the site); otherwise routes
 * to the contact form with the lab-demo subject. Completed bookings inside the
 * embed are logged as lab leads via the Calendly postMessage event.
 */
export const BookDemoButton = ({ className = "", testid = "book-demo-btn", label = "Book a demo appointment" }) => {
    const [open, setOpen] = useState(false);
    const nav = useNavigate();

    // Calendly fires `calendly.event_scheduled` via postMessage when the
    // visitor completes a booking inside the embedded iframe.
    useEffect(() => {
        if (!open) return;
        const onMsg = (e) => {
            if (typeof e.origin === "string" && e.origin.includes("calendly.com") && e.data?.event === "calendly.event_scheduled") {
                axios.post(`${API}/leads/calendly-booking`, {
                    page: window.location.pathname,
                    event_uri: e.data.payload?.event?.uri || "",
                    invitee_uri: e.data.payload?.invitee?.uri || "",
                }).catch(() => {});
                toast.success("Booking confirmed — see you at the demo!");
            }
        };
        window.addEventListener("message", onMsg);
        return () => window.removeEventListener("message", onMsg);
    }, [open]);

    const onClick = () => {
        if (CALENDLY_URL) { setOpen(true); return; }
        nav(`/contact?subject=demo-request-lab&message=${encodeURIComponent("Hi — I'd like to book a demo appointment for Toothmatrix Lab. My availability is …")}`);
    };

    return (
        <>
            <button type="button" onClick={onClick} data-testid={testid}
                className={className || "inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-slate-300 hover:border-cyan-500 text-slate-800 font-medium text-[13.5px] transition"}>
                <CalendarClock size={15} /> {label}
            </button>
            {open && CALENDLY_URL && (
                <div className="fixed inset-0 z-[100] bg-slate-950/70 backdrop-blur-sm grid place-items-center p-4" data-testid="book-demo-modal" onClick={() => setOpen(false)}>
                    <div className="relative bg-white rounded-2xl overflow-hidden w-full max-w-3xl h-[80vh]" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => setOpen(false)} data-testid="book-demo-close"
                            className="absolute top-3 right-3 z-10 p-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600">
                            <X size={15} />
                        </button>
                        <iframe title="Book a demo" src={`${CALENDLY_URL}?embed_domain=${window.location.hostname}&embed_type=PopupWidget`} className="w-full h-full border-0" />
                    </div>
                </div>
            )}
        </>
    );
};
